// Notes screen (`/notes`) — README §6.6.
//
// Renders prerequisites.skills[] and prerequisites.notes[]. Each card
// is collapsed by default; a small chef-hat affordance expands an
// inline panel with the longer description (here: the same `.text`
// rendered with more line spacing — v1 has no separate `description`
// field on PrerequisiteItem).
//
// If the URL hash matches a skill/note id on mount, that card is
// scrolled into view and expanded automatically — supports deep-links
// from the Ingredients/Utensils/Equipment chef-hat buttons.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, ChefHat, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';

interface Entry {
  id: string;
  text: string;
  source: 'skill' | 'note';
}

export default function Notes() {
  const navigate = useNavigate();
  const location = useLocation();
  const recipe = useAppStore((s) => s.recipe);
  const markVisited = useAppStore((s) => s.markVisited);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    markVisited('notes');
  }, [markVisited]);

  // Read the hash once on mount/navigation so a deep-link expands and
  // scrolls the matching card.
  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    setExpanded((prev) => ({ ...prev, [id]: true }));
    // Wait one frame so the card has rendered with the new id.
    const t = setTimeout(() => {
      const el = document.getElementById(`note-${id}`);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 0);
    return () => clearTimeout(t);
  }, [location.hash]);

  if (!recipe) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const skills = (recipe.cookpit.prerequisites?.skills ?? []).map<Entry>((s) => ({
    id: s.id,
    text: s.text,
    source: 'skill',
  }));
  const notes = (recipe.cookpit.prerequisites?.notes ?? []).map<Entry>((n) => ({
    id: n.id,
    text: n.text,
    source: 'note',
  }));
  const all = [...skills, ...notes];

  return (
    <main
      style={{
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '40rem',
        margin: '0 auto',
        minHeight: '100vh',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Wordmark size="sm" as="span" />
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Techniques &amp; terms.</h1>
      </header>

      {all.length === 0 && (
        <p style={{ color: theme.text.muted }}>No techniques or notes on this recipe.</p>
      )}

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        {all.map((entry) => {
          const isOpen = !!expanded[entry.id];
          return (
            <li
              key={entry.id}
              id={`note-${entry.id}`}
              style={{
                background: theme.bg.raised,
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: theme.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {entry.source === 'skill' ? 'Technique' : 'Note'}
                </div>
                <div style={{ fontWeight: 500, marginTop: '0.25rem' }}>{entry.text}</div>
                {isOpen && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: `1px solid ${theme.bg.muted}`,
                      color: theme.text.muted,
                      lineHeight: 1.6,
                    }}
                  >
                    {entry.text}
                  </div>
                )}
              </div>
              <ChefHat
                ariaLabel={isOpen ? `Collapse ${entry.id}` : `Expand ${entry.id}`}
                onClick={() => setExpanded((prev) => ({ ...prev, [entry.id]: !prev[entry.id] }))}
              />
            </li>
          );
        })}
      </ul>

      <div>
        <Button variant="secondary" onClick={() => navigate('/prereqs')}>
          Back to Prerequisites
        </Button>
      </div>
    </main>
  );
}
